import { SettingsPermissions } from "@redotech/account-common/permissions";
import type {
  SettingAccess,
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type ShippingCrossBorderFlatTeamSettingGroupId =
  "shipping_cross_border.cross_border";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason: "Landed-cost guarantees for labels bought outside Redo's OMS.",
};

// Reaching the page and editing it are two different permissions. The whole
// Shipping settings section — nav entry and every route under it — is gated on
// SET_SHIPPING_SETTINGS, so that is what reading has to demand; the card's own
// controls gate on SET_ORDER_FULFILLMENT_SETTINGS (see cross-border.tsx), so
// that is what writing has to demand.
const groupAccess: SettingAccess = {
  read: {
    minVisibility: "merchant",
    permission: SettingsPermissions.SET_SHIPPING_SETTINGS,
  },
  write: {
    mode: "grants",
    grants: [
      {
        minVisibility: "merchant",
        method: "direct",
        permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
      },
    ],
  },
};

export const ShippingCrossBorderFlatTeamSettingGroups = [
  {
    id: "shipping_cross_border.cross_border",
    title: "Cross border",
    description:
      "Guaranteed landed cost when labels are bought outside Redo's OMS, using the UPS Unlock workflow through Zonos.",
    productArea: "fulfillment",
    access: groupAccess,
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "cross-border" },
    synonyms: ["cross border", "shipping cross border"],
  },
] as const satisfies readonly SettingGroupDefinition<ShippingCrossBorderFlatTeamSettingGroupId>[];

type _assertAllShippingCrossBorderFlatTeamSettingGroupIdsProvided =
  ShippingCrossBorderFlatTeamSettingGroupId extends (typeof ShippingCrossBorderFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allShippingCrossBorderFlatTeamSettingGroupIdsProvided: _assertAllShippingCrossBorderFlatTeamSettingGroupIdsProvided = true;
void _allShippingCrossBorderFlatTeamSettingGroupIdsProvided;

export const ShippingCrossBorderFlatTeamSettingGroupById: Record<
  ShippingCrossBorderFlatTeamSettingGroupId,
  SettingGroupDefinition<ShippingCrossBorderFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      ShippingCrossBorderFlatTeamSettingGroupId,
      SettingGroupDefinition<ShippingCrossBorderFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of ShippingCrossBorderFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    ShippingCrossBorderFlatTeamSettingGroupId,
    SettingGroupDefinition<ShippingCrossBorderFlatTeamSettingGroupId>
  >;
})();
